import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import { ACHFields } from 'common/components/payment/ach-fields';
import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { Radio } from 'core/components/radio';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import { PolicyTabs } from 'core/store/account-store/policies-store/policy-store/policy-store';
import CreditCardForm from 'customer/components/policy/payment-tab/credit-card-form/credit-card-form';
import useStyles from './add-secondary-credit-card-modal.styles';

const PaymentMethodOption = { Card: 'card', BankAccount: 'ach' };

const AddSecondaryCreditCardModal = ({ open, onClose }) => {
  const classes = useStyles();
  const toast = useToast();
  const { values } = useFormikContext();
  const { isAgency, user } = useSession();
  const [selectedMethod, setSelectedMethod] = useState(PaymentMethodOption.Card);

  const {
    account: {
      fetchFullAccountAndPolicy,
      id: accountId,
      policies: {
        policy: {
          policy: { id },
          billingDetails,
          addCreditCard,
          addBankAccount,
          setCurrentTab,
          isCancelledOrInCancellation
        }
      }
    }
  } = useStore();
  const { getBasisTheoryCCToken, getBasisTheoryACHToken } = useBasisTheoryToken();

  // A bank account can't be an alternate method while the policy is cancelled / in cancellation —
  // only a card can be added during the reinstatement window.
  const isBankAccount = selectedMethod === PaymentMethodOption.BankAccount;
  const bankAccountBlocked = isBankAccount && isCancelledOrInCancellation;

  // ACHFields writes these validity flags to Formik on each change event; gate the Add button on
  // them so we never tokenize an incomplete BasisTheory element.
  const achFieldsValid = values.isRoutingNumberValid && values.isAccountNumberValid && values.isAccountHolderNameValid;

  const handleAddCard = async () => {
    try {
      const { token } = await getBasisTheoryCCToken();
      const { success, message, card } = await addCreditCard({
        accountId,
        policyId: id,
        basisTheoryCardToken: token,
        customerId: values.stripeCustomerId,
        isPrimary: false
      });
      const externalAgentLabelText =
        isCancelledOrInCancellation && isAgency
          ? 'To process payments, contact support.'
          : 'To process payments, go to payment and price tab.';

      toast.notify({
        type: success ? 'success' : 'error',
        message,
        label: externalAgentLabelText,
        action: () => setCurrentTab(PolicyTabs.PAYMENT),
        durationInSeconds: 10
      });

      if (success && values.stripeCustomerId !== card.customerId) {
        fetchFullAccountAndPolicy(id, accountId);
      }
      // The card branch closes via CreditCardForm's own `fromModal` → closeModal() call.
    } catch (error) {
      toast.notify({
        type: 'error',
        message: `An error occurred. ${error.message.replace(':', '').trim()}`
      });
    }
  };

  const handleAddBankAccount = async () => {
    try {
      const basisTheoryBankAccountTokenInfo = await getBasisTheoryACHToken();
      const last4 = basisTheoryBankAccountTokenInfo.data.account_number.slice(-4);

      // alternate bank accounts are saved to the profile but don't replace the default
      const bankAccountExists = billingDetails?.allPaymentMethods?.find(
        (existingPayment) =>
          last4.endsWith(existingPayment.last4) &&
          (existingPayment.id?.startsWith('ba') ||
            existingPayment.type === 'us_bank_account' ||
            existingPayment.bankName)
      );
      if (bankAccountExists) {
        throw new Error(`Bank account ending in ${last4} already exists`);
      }

      const accountData = {
        id: basisTheoryBankAccountTokenInfo.id,
        basisTheoryBankAccountToken: basisTheoryBankAccountTokenInfo.id,
        routingNumber: basisTheoryBankAccountTokenInfo.data.routing_number,
        last4
      };

      track('Staff - Changed Payment Method', {
        oldPaymentMethod: billingDetails?.activePaymentMethod?.bankName ? 'ACH' : 'CC',
        newPaymentMethod: 'ACH',
        last4,
        policyId: id,
        customerId: accountId,
        staffUser: user.username
      });
      addBankAccount(accountData);
      toast.notify({
        type: 'success',
        message: `Bank account ending in ${last4} added`,
        label: 'To process payments with this method, go to the payment and price tab after adding it.',
        action: () => setCurrentTab(PolicyTabs.PAYMENT),
        durationInSeconds: 10
      });
      onClose();
    } catch (error) {
      toast.notify({ type: 'error', message: error.message });
    }
  };

  const userTip =
    isAgency && isCancelledOrInCancellation
      ? 'To process payments with this method, contact support after adding it, since this policy is in cancellation or has been canceled.'
      : 'To process payments with this method, go to the payment and price tab after adding it.';

  const alternateMethodCallout = (
    <Grid container className={classes.callouts}>
      <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
        <Label type="body3">
          This payment method will be saved to the member’s profile for future use. It won’t replace the primary method
          or trigger an immediate charge. Use an alternate payment method when you need to make a one-time payment using
          a different method than the primary one.
        </Label>
      </NotificationCard>
      <div className={classes.tip}>
        <img src={infoDarkGreen} alt="info" />
        <Label type="notificationText">
          <span className={classes.tipLabel}>User tip:</span> {userTip}
        </Label>
      </div>
    </Grid>
  );

  return (
    <BaseDialog open={open} onClose={onClose} className={classes.container}>
      <div className={classes.content}>
        <Label type="h1" className={classes.title}>
          Add a secondary payment method
        </Label>
        <div className={classes.methodChooser}>
          <Label type="body3">What type of additional method of payment would you like to add?</Label>
          <Radio
            id="alternate-payment-method"
            name="alternate-payment-method"
            labelClassName={classes.radioLabel}
            value={selectedMethod}
            onChange={(e) => setSelectedMethod(e.target.value)}
            options={[
              { value: PaymentMethodOption.Card, label: 'Card' },
              { value: PaymentMethodOption.BankAccount, label: 'Bank account' }
            ]}
          />
        </div>
        {selectedMethod === PaymentMethodOption.Card && (
          <div>
            <CreditCardForm
              onSubmit={handleAddCard}
              buttonText="Add"
              label="Card information"
              mode="dark"
              fromModal
              closeModal={onClose}
            >
              {alternateMethodCallout}
            </CreditCardForm>
          </div>
        )}
        {isBankAccount && (
          <>
            {bankAccountBlocked ? (
              <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
                <Label type="body3">
                  You can’t add a bank account as an alternate payment method right now. Bank accounts aren’t permitted
                  while the policy is in cancellation or pending cancellation. To continue, please add a card instead.
                </Label>
              </NotificationCard>
            ) : (
              <>
                <div className="HIDDEN_FROM_SCREEN_SHARING">
                  <ACHFields mode="light" />
                </div>
                {alternateMethodCallout}
              </>
            )}
            <div className={classes.actions}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleAddBankAccount}
                disabled={bankAccountBlocked || !achFieldsValid}
              >
                Add
              </Button>
            </div>
          </>
        )}
      </div>
    </BaseDialog>
  );
};

AddSecondaryCreditCardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default observer(AddSecondaryCreditCardModal);
